/**
 * Redux slice for representative and shareholder/director information
 * Manages authorized representative details and company ownership structure
 */

import { CompanyRole } from "@funded-here-interface/common/src/constant/enum";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ShareholderDirector {
  role: CompanyRole;
  firstName: string;
  lastName: string;
  email: string;
}

interface RepState {
  repFirstName: string | null;
  repLastName: string | null;
  repEmail: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  repTitle: string | null;
  merchantTitle: string | null;
  shareholdersDirectors: ShareholderDirector[];
}

const initialState: RepState = {
  repFirstName: null,
  repLastName: null,
  repEmail: null,
  userFirstName: null,
  userLastName: null,
  repTitle: null,
  merchantTitle: null,
  shareholdersDirectors: [],
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof RepState>(field: K) => {
  return (state: RepState, action: PayloadAction<RepState[K]>) => {
    state[field] = action.payload;
  };
};

const repSlice = createSlice({
  name: "rep",
  initialState,
  reducers: {
    // Representative information setters
    setRepFirstName: createFieldSetter("repFirstName"),
    setRepLastName: createFieldSetter("repLastName"),
    setRepEmail: createFieldSetter("repEmail"),

    // User information setters
    setUserFirstName: createFieldSetter("userFirstName"),
    setUserLastName: createFieldSetter("userLastName"),

    // Title setters
    setRepTitle: createFieldSetter("repTitle"),
    setMerchantTitle: createFieldSetter("merchantTitle"),

    // Shareholder/Director setter
    setShareholderDirector: createFieldSetter("shareholdersDirectors"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateRepState: (state, action: PayloadAction<Partial<RepState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetRepState: () => initialState,
  },
});

export const {
  setRepFirstName,
  setRepLastName,
  setRepEmail,
  setUserFirstName,
  setUserLastName,
  setRepTitle,
  setMerchantTitle,
  setShareholderDirector,
  hydrateRepState,
  resetRepState,
} = repSlice.actions;

export default repSlice.reducer;
